import { Box, CheckboxGroup, HStack, useColorMode, VStack } from '@chakra-ui/react'
import { useFlags } from '@missionlabs/feature-flags'
import {
    Body,
    Checkbox,
    Dropdown,
    ErrorMessage,
    Input,
    useDebouncedFunction,
    useFormContext,
    useSyncedState
} from '@missionlabs/react'
import { AlwaysForwardType, UserCallRoute } from '@missionlabs/types'
import { InterstitialRadio } from 'features/settings/components/InterstitialRadio'
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef } from 'react'
import { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useFormatToNumberE164 } from 'shared/hooks/useFormatToNumberE164'

import { getTimeoutOptions } from './timeoutOptions'

type RadioType = 'inHoursUnanswered' | 'outOfHours' | 'busy'

const propNames = {
    busy: { forward: 'busy', redirect: 'busyRedirect' },
    inHoursUnanswered: {
        forward: 'inHoursUnanswered',
        redirect: 'unansweredRedirect'
    },
    outOfHours: {
        forward: 'outOfHours',
        redirect: 'outOfHoursRedirect'
    }
} as const

type InHoursProps = {
    type: 'inHoursUnanswered'
    callRoute: Pick<
        UserCallRoute,
        'inHoursUnanswered' | 'unansweredRedirect' | 'callTimeout' | 'always'
    >
}

type OutOfHoursProps = {
    type: 'outOfHours'
    callRoute: Pick<UserCallRoute, 'outOfHours' | 'outOfHoursRedirect' | 'always'>
}

type BusyProps = {
    type: 'busy'
    callRoute: Pick<UserCallRoute, 'busy' | 'busyRedirect' | 'always'>
}

type RedirectExternalRadioProps = (InHoursProps | OutOfHoursProps | BusyProps) & {
    type: RadioType
    forwardType: string
    onUpdate: (payload: Partial<UserCallRoute>) => void
    minW?: string
    hideRadio?: boolean
    presentAlways?: boolean
    onNumberValidation?: (isNumberValid: boolean) => void
    callTimeoutError?: FieldError
}

export const RedirectExternalRadio: FC<RedirectExternalRadioProps> = ({
    type,
    forwardType,
    callRoute,
    onUpdate,
    hideRadio = false,
    presentAlways = true,
    onNumberValidation,
    callTimeoutError,
    minW
}) => {
    const { call_forwarding_always } = useFlags(['call_forwarding_always'])

    const { t } = useTranslation()
    const { colorMode } = useColorMode()
    const formContext = useFormContext()
    const { watch, setValue } = formContext || {}
    const { formatToNumberE164, isValidPhoneNumber } = useFormatToNumberE164()

    const ref = useRef<HTMLInputElement>(null)

    const isActive = useMemo(() => forwardType === 'redirect', [forwardType])

    const [callTimeout, setCallTimeout] = useSyncedState(
        type === 'inHoursUnanswered' ? callRoute.callTimeout : undefined
    )
    const [number, setNumber] = useSyncedState(
        callRoute[propNames[type].redirect]?.numberE164 ?? ''
    )
    const [announce, setAnnounce] = useSyncedState(callRoute[propNames[type].redirect]?.announce)
    const [flipCLI, setFlipCLI] = useSyncedState(callRoute[propNames[type].redirect]?.flipCLI)
    const [always, setAlways] = useSyncedState(
        (formContext ? watch('callRoute.always') : callRoute.always) === 'redirect'
    )

    const isValidNumber = useMemo(() => {
        const result = isValidPhoneNumber(number)
        if (onNumberValidation) onNumberValidation(result)
        return result
    }, [isValidPhoneNumber, number, onNumberValidation])

    const checked = useMemo(() => {
        const checked: string[] = []
        if (announce) checked.push('announceCalls')
        if (flipCLI) checked.push('presentDialledNumber')
        if (always) checked.push('always')
        return checked
    }, [announce, flipCLI, always])

    const debouncedUpdate = useDebouncedFunction(
        (payload: Partial<UserCallRoute>) => onUpdate(payload),
        500
    )

    const handleFocus = () => {
        if (!ref.current) return
        ref.current.focus()
    }

    const handleNumberChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => setNumber(e.target.value),
        [setNumber]
    )
    const handleFlipCLI = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => setFlipCLI(e.target.checked),
        [setFlipCLI]
    )
    const handleAnnounce = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => setAnnounce(e.target.checked),
        [setAnnounce]
    )
    const handleAlways = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (formContext) {
                setAlways(e.target.checked)
                setValue('callRoute.always', e.target.checked ? 'redirect' : 'inactive')
            }
            setAlways(e.target.checked)
        },
        [setAlways, setValue, formContext]
    )

    const handleCallTimeoutChange = useCallback(
        (value: string) => {
            const newTimeout = parseInt(value, 10)
            setCallTimeout(newTimeout)
            setValue('callTimeout', newTimeout)
        },
        [setCallTimeout, setValue]
    )

    useEffect(() => {
        if (!isActive || !isValidNumber) return

        const redirectBody = {
            flipCLI,
            announce,
            numberE164: formatToNumberE164(number)
        }
        const updateObject: Partial<UserCallRoute> = {
            [propNames[type].forward]: 'redirect',
            always: (always ? 'redirect' : 'inactive') as AlwaysForwardType,
            [propNames[type].redirect]: redirectBody,
            ...(always ? { alwaysRedirect: redirectBody } : {})
        }

        if (type === 'inHoursUnanswered') {
            updateObject['callTimeout'] = callTimeout
        }

        debouncedUpdate(updateObject)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [announce, callTimeout, flipCLI, number, always, type])

    const contents = useMemo(
        () => (
            <VStack align="flex-start" spacing="8px">
                <HStack spacing="16px" w="full" alignItems="start">
                    {type === 'inHoursUnanswered' && (
                        <Box w="full" maxW="196px">
                            <Dropdown
                                w="full"
                                options={getTimeoutOptions(t)}
                                value={callTimeout}
                                onChange={handleCallTimeoutChange}
                            />
                            {!!callTimeoutError && <ErrorMessage error={callTimeoutError} />}
                        </Box>
                    )}
                    <Box>
                        <Input
                            ref={ref}
                            aria-label="redirect number"
                            maxW="296px"
                            value={number}
                            onChange={handleNumberChange}
                            onClick={handleFocus}
                        />
                        {!isValidNumber && (
                            <Body sx={{ color: `${colorMode}.alerts.red`, py: '8px' }} size="sm">
                                {t('validations.number')}
                            </Body>
                        )}
                    </Box>
                </HStack>
                <CheckboxGroup value={checked}>
                    <Checkbox key="announceCalls" value="announceCalls" onChange={handleAnnounce}>
                        <Body as="span" size="sm" sx={{ color: `${colorMode}.coolGrey.80` }}>
                            {t('calls.announceCalls')}
                        </Body>
                    </Checkbox>
                    <Checkbox
                        key="presentDialledNumber"
                        value="presentDialledNumber"
                        onChange={handleFlipCLI}
                    >
                        <Body as="span" size="sm" sx={{ color: `${colorMode}.coolGrey.80` }}>
                            {t('calls.presentDialledNumber')}
                        </Body>
                    </Checkbox>
                    {call_forwarding_always.enabled && presentAlways && (
                        <Checkbox
                            key="always"
                            value="always"
                            checked={always}
                            onChange={handleAlways}
                        >
                            <Body as="span" size="sm" sx={{ color: `${colorMode}.coolGrey.80` }}>
                                {t('calls.always')}
                            </Body>
                        </Checkbox>
                    )}
                </CheckboxGroup>
            </VStack>
        ),
        [
            always,
            callTimeout,
            callTimeoutError,
            call_forwarding_always.enabled,
            checked,
            colorMode,
            handleAlways,
            handleAnnounce,
            handleFlipCLI,
            handleNumberChange,
            handleCallTimeoutChange,
            isValidNumber,
            number,
            presentAlways,
            t,
            type
        ]
    )

    return !hideRadio ? (
        <InterstitialRadio
            value="redirect"
            current={forwardType}
            onChange={() => onUpdate({ [propNames[type].forward]: 'redirect' })}
            label={t('calls.redirectExternal')}
            minW={minW}
            alignItems="center"
        >
            {contents}
        </InterstitialRadio>
    ) : (
        contents
    )
}
